import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { SUB_FACILITIES } from 'constants/endpoints';
import { METHOD_PUT } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { ApiRequestConfigType } from 'requests/index.types';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { RemoveCustomerLocationDialogProps } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/RemoveCustomerLocationDialog/RemoveCustomerLocationDialog.types';
import { toastService } from 'services';
import { useTranslation } from 'hooks/useTranslation';
import { useParams } from 'react-router-dom';
import { useCustomersSettingsPageDataContext } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.data.context';
import { isNewLocationId } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.constants';

interface RemoveCustomerLocationDialogForm {
  handleRemoveCustomerLocation: () => void;
  locationName?: string;
}

const API_PUT_RemoveSubFacilityAddress = (
  community_id: string,
  sub_facility_id: string,
  address_id: string,
): ApiRequestConfigType => ({
  __typename: 'LegacyAPI',
  url: `${SUB_FACILITIES(community_id)}/${sub_facility_id}`,
  method: METHOD_PUT,
  body: {
    sub_facility: {
      sub_facility_addresses_attributes: [
        {
          id: address_id,
          _destroy: true,
        },
      ],
    },
  },
});

export const RemoveCustomerLocationDialogFormContext = createContext(
  {} as RemoveCustomerLocationDialogForm,
);

export const RemoveCustomerLocationDialogFormProvider: FC<RemoveCustomerLocationDialogProps> = ({
  children,
  addressId,
  onRemoveComplete,
  locationName,
}) => {
  const { apiRequest } = useAPIActions();
  const { t } = useTranslation();
  const { locationId, customerId } = useParams<{ locationId: string; customerId: string }>();
  const { fetch: refetchCustomers } = useCustomersSettingsPageDataContext();

  const handleRemoveCustomerLocation = useCallback(async () => {
    if (isNewLocationId(addressId)) {
      onRemoveComplete();
    } else {
      await apiRequest(API_PUT_RemoveSubFacilityAddress(locationId, customerId, addressId), {
        onSuccess: () => {
          toastService.success(t('location_settings:REMOVE_CUSTOMER_LOCATION_SUCCESS'));
          refetchCustomers();
          onRemoveComplete();
        },
        onError: handleGenericErrorMessage,
      });
    }
  }, [apiRequest, onRemoveComplete, locationId, customerId, addressId, t, refetchCustomers]);

  const contextValue = useMemo(
    () => ({
      handleRemoveCustomerLocation,
      locationName,
    }),
    [handleRemoveCustomerLocation, locationName],
  );

  return (
    <RemoveCustomerLocationDialogFormContext.Provider value={contextValue}>
      {children}
    </RemoveCustomerLocationDialogFormContext.Provider>
  );
};

export const useRemoveCustomerLocationDialogFormContext = () =>
  useContext(RemoveCustomerLocationDialogFormContext);
